import { useMemo } from "react";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Brush,
} from "recharts";

// b-coldtrack-style temperature-history chart (recharts). Presentational only:
// the caller supplies already-downsampled rows + which series/reference lines
// to draw. Threshold + measured-sensor lines, a shaded band under the primary
// series, dashed reference lines, and a horizontal zoom/scroll brush.

export interface ChartSeries {
  key: string; // row field to plot (e.g. "insideTemp")
  name: string; // legend/tooltip label (already translated)
  color: string;
  primary?: boolean; // draw as a gradient area (only the first primary gets fill)
  // Draw as a dashed step line — for the per-record threshold/set-point series
  // (high/low alarm, set point) which change over time and must follow the data,
  // not sit as a constant reference line.
  dashed?: boolean;
}

export interface ChartRefLine {
  value: number;
  label: string;
  color: string;
  // Draw only the right-edge label (no line) — used to tag the per-record
  // threshold STEP series with "สูง 39°"-style markers at their latest value
  labelOnly?: boolean;
}

interface Props {
  data: any[]; // rows carrying `timeKey` + each series key
  series: ChartSeries[];
  refLines?: ChartRefLine[];
  timeKey?: string; // field holding the timestamp (string or ms). default "time"
  height?: number;
  unitSuffix?: string; // e.g. "°C"
  hideBrush?: boolean; // true while snapshotting for the PDF (clean ref-style chart)
}

const toMs = (v: any): number =>
  typeof v === "number" ? v : dayjs(v).valueOf();

// Aggregated rows can carry "-" / "" / null for missing samples; coerce to a
// real number or null. The lines DON'T connect across nulls (connectNulls=false)
// so a minute with no data reads as a real gap, which is what operators inspect
// for — a flat line bridging missing data would hide the outage.
const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "-" || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

export default function ChartTemperatureHistory({
  data,
  series,
  refLines = [],
  timeKey = "time",
  // compact banner height — the owner's tuned value from b-coldtrack
  height = 160,
  unitSuffix = "",
  hideBrush = false,
}: Props) {
  // recharts needs a numeric time axis for scale="time"; series values are
  // coerced to number|null so bad cells don't break the line.
  const chartData = useMemo(
    () =>
      data.map((d) => {
        const row: any = { __t: toMs(d[timeKey]) };
        series.forEach((s) => {
          row[s.key] = toNum(d[s.key]);
        });
        return row;
      }),
    [data, series, timeKey]
  );

  // Show the date on ticks only when the series spans more than one calendar
  // day (weeks/months); otherwise just HH:mm.
  const multiDay = useMemo(() => {
    if (chartData.length < 2) return false;
    const first = chartData[0].__t;
    const last = chartData[chartData.length - 1].__t;
    return !dayjs(first).isSame(dayjs(last), "day");
  }, [chartData]);

  const tickFormatter = (t: number) =>
    multiDay ? dayjs(t).format("DD/MM HH:mm") : dayjs(t).format("HH:mm");

  const primaryKey = series.find((s) => s.primary)?.key;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={chartData}
        margin={{ top: 10, right: 48, bottom: 4, left: 0 }}
      >
        <defs>
          <linearGradient id="tempHistGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.12} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="__t"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          tickFormatter={tickFormatter}
          tick={{ fontSize: 8, fill: "#94a3b8" }}
          tickLine={false}
          axisLine={false}
          minTickGap={40}
        />
        <YAxis
          // Fit the axis to the series actually displayed (chartData only
          // carries visible series), with a little headroom — hiding the
          // threshold lines zooms the sensor detail instead of wasting range.
          domain={[
            (dataMin: number) => Math.floor(dataMin - 1),
            (dataMax: number) => Math.ceil(dataMax + 1),
          ]}
          tick={{ fontSize: 8, fill: "#94a3b8" }}
          tickLine={false}
          axisLine={false}
          tickCount={5}
          width={36}
          tickFormatter={(v) => `${v}${unitSuffix}`}
        />
        {/* no tooltip/cursor while snapshotting — otherwise a lingering hover
            leaves a gray vertical cursor line baked into the exported image */}
        {hideBrush ? null : (
          <Tooltip
            labelFormatter={(t) =>
              dayjs(t as number).format("DD/MM/YYYY HH:mm")
            }
            formatter={(value: any, name: any) => [
              value === null || value === undefined
                ? "-"
                : `${value}${unitSuffix}`,
              name,
            ]}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            // keep the tooltip above neighboring content (e.g. the sticky
            // table header below the chart overlapped it at default stacking)
            wrapperStyle={{ zIndex: 20 }}
          />
        )}
        {refLines.map((r, i) => (
          <ReferenceLine
            key={`ref-${i}`}
            y={r.value}
            stroke={r.labelOnly ? "none" : r.color}
            strokeDasharray="4 3"
            label={{
              value: r.label,
              position: "right",
              fill: r.color,
              fontSize: 7,
              fontWeight: 600,
            }}
          />
        ))}
        {/* gradient band under the primary series, drawn first so it sits at
            the bottom and doesn't veil the other lines */}
        {primaryKey ? (
          <Area
            type="monotone"
            dataKey={primaryKey}
            stroke="none"
            fill="url(#tempHistGrad)"
            connectNulls={false}
            dot={false}
            // recharts enables activeDot by default; disable it here too or a
            // lingering hover bakes a white circle onto the gradient band (most
            // visible right at a data-gap break)
            activeDot={false}
            isAnimationActive={false}
            legendType="none"
            tooltipType="none"
          />
        ) : null}
        {/* lines rendered in reverse so the first series (e.g. inside temp)
            sits topmost, matching the legend order */}
        {[...series].reverse().map((s) => (
          <Area
            key={s.key}
            type={s.dashed ? "stepAfter" : "monotone"}
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={s.dashed ? 1 : s.primary ? 2.2 : 1.3}
            strokeDasharray={s.dashed ? "4 3" : undefined}
            fill="none"
            connectNulls={false}
            dot={false}
            // no active (hover) dots while snapshotting — otherwise a lingering
            // hover bakes stray dots on every series into the exported image
            activeDot={hideBrush ? false : { r: 2, strokeWidth: 0 }}
            isAnimationActive={false}
          />
        ))}
        {hideBrush ? null : (
          <Brush
            dataKey="__t"
            height={20}
            travellerWidth={6}
            stroke="#cbd5e1"
            fill="#f8fafc"
            tickFormatter={tickFormatter}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}
